import { format, parseISO } from 'date-fns'
import React, { useContext, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { BlastContext } from '../../providers/blast/BlastContext'
import { environmentService } from '../../providers/environment/EnvironmentService'
import { unSubscribe } from '../../utils/Utils'
import { ActiveUsersState, toggleActiveUsers } from '../active-fe-users/activeUsersToggleSlice'
import {
    CloseIconStyled,
    DetailColumnStyled,
    DetailHeaderStyled,
    DetailTableStyled,
    DisplayFeContainerStyled,
    DisplayFeDetailsStyled,
    FeDetailsStyled,
    FeHeaderDetailsStyled,
    FeUsersStyled,
    MessageStyled,
    IconButton,
} from '../active-fe-users/styled'
import { ElementWithTooltip } from '../common/element-with/element-with-tooltip/ElementWithTooltip'
import { SeparatorStyled } from '../common/lookup/styled'
import {
    BlastConnectedStyled,
    UserAvatarStyled,
    UserAvatarWrapperStyled,
    UserInitialStyled,
    UserInitialWrapperStyled,
    UsersStyled,
} from './styled'
import { SessionContext } from '../../providers/session/SessionContext'
import { sessionService } from '../../providers/session/SessionService'
import { DividerStyled, SeparatorBorderStyled } from '../common/global-search/results/styled'

interface BOUserDetailsDO {
    id: string
    avatar?: string
    username?: string
    nickname?: string
    since?: string
    properties?: any
}

export const ActiveUsers = (): React.ReactElement => {
    const blastContext = React.useContext(BlastContext)
    const sessionContext = useContext(SessionContext)

    const [showActiveUsers] = useState<boolean>(() => {
        return environmentService.getEnvironment()?.showActiveUsers || false
    })

    const [showAvatar] = useState<boolean>(() => {
        return environmentService.getEnvironment()?.showActiveUserAvatar || false
    })
    const state: ActiveUsersState = useAppSelector((state) => state.activeUsersToggleSlice)
    const dispatch = useAppDispatch()

    const [users, setUsers] = useState<BOUserDetailsDO[]>([])
    const [selectedUser, setSelectedUser] = useState<BOUserDetailsDO | null>(null)

    const [blastConnected, setBlastConnected] = useState<boolean>(false)

    useEffect(() => {
        const activeUsersSubscription = blastContext.observeCollection('active.users').subscribe((data: any[]) => {
            // log.debug('ActiveUsers', 'MSG001', 'logged in users', data)
            const innerSelectedUser = data?.find((x) => x?.id === sessionContext?.getLoggedInUser()?.id)
            if (innerSelectedUser) {
                setSelectedUser(innerSelectedUser)
            }
            setUsers(data)
        })
        const blastConnectionSubscription = blastContext.observeConnection().subscribe((connected: boolean) => {
            setBlastConnected(connected)
        })
        return () => {
            unSubscribe(activeUsersSubscription)
            unSubscribe(blastConnectionSubscription)
        }
    }, [blastContext])

    const logoutHandler = (session: any) => {
        sessionContext
            .signOut(session)
            .then(() => {})
            .catch((error: any) => console.error('logout error', error))
    }

    const logoutAllSessionsHandler = (sessions: any) => {
        sessions?.map((session: any) => {
            logoutHandler(session)
        })
    }

    const renderDisconnected = (): React.ReactElement => {
        return (
            <>
                <BlastConnectedStyled connected={false}>
                    <div>Blast disconnected</div>
                </BlastConnectedStyled>
            </>
        )
    }

    function capitalizeFirstLetter(input: string): string {
        if (input) {
            if (input.length === 0) {
                return input
            }
            return input.charAt(0).toUpperCase() + input.slice(1)
        }
        return input
    }

    const displayUserDetails = (): React.ReactElement => {
        if (users.length === 0) {
            return <MessageStyled>No admin users logged in.</MessageStyled>
        }

        const isCurrentUser = selectedUser?.id === sessionContext?.getLoggedInUser()?.id

        return (
            <>
                <DetailTableStyled numberRows={1}>
                    <thead>
                        <tr>
                            <DetailHeaderStyled>&nbsp;</DetailHeaderStyled>
                            <DetailHeaderStyled>{selectedUser?.nickname ? 'Nickname' : 'Username'}</DetailHeaderStyled>
                            <DetailHeaderStyled>First Name</DetailHeaderStyled>
                            <DetailHeaderStyled>Last Name</DetailHeaderStyled>
                        </tr>
                    </thead>
                    <tbody>
                        <tr key={selectedUser?.id}>
                            <DetailColumnStyled>
                                <UserInitialWrapperStyled key={selectedUser?.id}>
                                    <UserInitialStyled>
                                        {selectedUser?.nickname?.substring(0, 1) || selectedUser?.username?.substring(0, 1) || ''}
                                    </UserInitialStyled>
                                </UserInitialWrapperStyled>
                            </DetailColumnStyled>
                            <DetailColumnStyled>{selectedUser?.nickname || selectedUser?.username}</DetailColumnStyled>
                            <DetailColumnStyled>{`${
                                selectedUser?.properties ? selectedUser?.properties['firstName'] || '' : ''
                            }`}</DetailColumnStyled>
                            <DetailColumnStyled>{`${
                                selectedUser?.properties ? selectedUser?.properties['lastName'] || '' : ''
                            }`}</DetailColumnStyled>
                        </tr>
                    </tbody>
                </DetailTableStyled>
                <SeparatorBorderStyled />
                {isCurrentUser && (
                    <ElementWithTooltip
                        tooltip={<span>{'Logout all sessions'}</span>}
                        placement={'right-end'}
                        element={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', marginRight: '44px' }}>
                                <DetailColumnStyled>Logout all sessions</DetailColumnStyled>
                                <IconButton type="button" onClick={() => logoutAllSessionsHandler(selectedUser?.properties['sessions'])}>
                                    <i className="fa-solid fa-power-off" />
                                </IconButton>
                            </div>
                        }
                    />
                )}
                <SeparatorStyled />
                <DetailTableStyled numberRows={1} style={{ marginTop: '16px' }} key={`${new Date().getTime()}`}>
                    <thead>
                        <tr>
                            <DetailHeaderStyled>Session</DetailHeaderStyled>
                            <DetailHeaderStyled>Device</DetailHeaderStyled>
                            <DetailHeaderStyled>Sign-in</DetailHeaderStyled>
                            <DetailHeaderStyled>IP Adress</DetailHeaderStyled>
                            <DetailHeaderStyled>URL</DetailHeaderStyled>
                            <DetailHeaderStyled>Actions</DetailHeaderStyled>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedUser?.properties &&
                            selectedUser?.properties['sessions']?.map((session: any, idx: number) => {
                                return (
                                    <tr key={`tr-${idx}`}>
                                        <DetailColumnStyled>{idx + 1}</DetailColumnStyled>
                                        <DetailColumnStyled>{capitalizeFirstLetter(session?.device)}</DetailColumnStyled>
                                        <DetailColumnStyled>
                                            {session.dateCreated ? format(parseISO(session?.dateCreated), 'yyyy-MM-dd HH:mm:ss') : ''}
                                        </DetailColumnStyled>
                                        <DetailColumnStyled>{session?.ipAddress}</DetailColumnStyled>
                                        <DetailColumnStyled
                                            align="left"
                                            style={{
                                                maxWidth: 300,
                                                // whiteSpace: 'nowrap',
                                                // overflow: 'hidden',
                                                // textOverflow: 'ellipsis',
                                                // direction: 'rtl'
                                            }}
                                        >
                                            {session?.sessionUrl || '/account'}
                                        </DetailColumnStyled>
                                        <DetailColumnStyled>
                                            {(isCurrentUser && session?.isLoginSession === false) && (
                                                <ElementWithTooltip
                                                    tooltip={<span>{'Logout'}</span>}
                                                    placement={'bottom'}
                                                    element={
                                                        <IconButton type="button" onClick={() => logoutHandler(session)}>
                                                            <i className="fa-solid fa-power-off" />
                                                        </IconButton>
                                                    }
                                                />
                                            )}
                                        </DetailColumnStyled>
                                    </tr>
                                )
                            })}
                    </tbody>
                </DetailTableStyled>
            </>
        )
    }
    const showUserDetails = (): React.ReactElement => {
        return (
            <DisplayFeContainerStyled>
                <DisplayFeDetailsStyled>
                    <FeHeaderDetailsStyled>
                        Current logged in admin users
                        <CloseIconStyled
                            role={'button'}
                            onClick={() =>
                                dispatch(
                                    toggleActiveUsers({
                                        showFEUsers: false,
                                        showBOUsers: false,
                                    })
                                )
                            }
                        />
                    </FeHeaderDetailsStyled>
                    <FeDetailsStyled>{displayUserDetails()}</FeDetailsStyled>
                </DisplayFeDetailsStyled>
            </DisplayFeContainerStyled>
        )
    }

    const renderAvatars = (user: BOUserDetailsDO): React.ReactElement => {
        return showAvatar ? (
            <UserAvatarStyled key={user.id} src={user.avatar} alt={user.username} />
        ) : (
            <UserInitialWrapperStyled key={user.id}>
                <ElementWithTooltip
                    tooltip={<span>{user.nickname || user.username || ''}</span>}
                    placement={'bottom'}
                    element={
                        <UserInitialStyled>{user.nickname?.substring(0, 1) || user.username?.substring(0, 1) || ''}</UserInitialStyled>
                    }
                />
            </UserInitialWrapperStyled>
        )
    }

    const renderSummary = (): React.ReactElement => {
        return (
            <>
                <FeUsersStyled
                    onClick={() =>
                        dispatch(
                            toggleActiveUsers({
                                showFEUsers: false,
                                showBOUsers: true,
                            })
                        )
                    }
                >
                    {users.length + (users.length === 0 ? ' Admin' : ' Admin')}{' '}
                </FeUsersStyled>
            </>
        )
    }

    const renderUsers = (): React.ReactElement => {
        return (
            <>
                <UsersStyled>
                    {users?.length > 10
                        ? renderSummary()
                        : users.map((user) => (
                              <UserAvatarWrapperStyled
                                  key={user.id}
                                  onClick={() => {
                                      if (!selectedUser?.properties['sessions']) {
                                          blastContext?.login(sessionContext?.getJwtSession()?.sessionToken || '')
                                      }

                                      setSelectedUser(user)
                                      dispatch(
                                          toggleActiveUsers({
                                              showFEUsers: false,
                                              showBOUsers: !state.showBOUsers,
                                          })
                                      )
                                  }}
                              >
                                  {renderAvatars(user)}
                              </UserAvatarWrapperStyled>
                          ))}
                </UsersStyled>
                {state.showBOUsers && showUserDetails()}
            </>
        )
    }

    if (!showActiveUsers) {
        return <></>
    }
    return <>{blastConnected ? renderUsers() : renderDisconnected()}</>
}
